/**
 * GuidedModeView — Step-by-step guided canvas filling with AI suggestions.
 *
 * Walks users through each BMC section with contextual questions,
 * AI-generated suggestion chips, and free-text input.
 */

import { useState, useCallback } from 'react'
import {
  Compass,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Loader2,
  RefreshCw,
  Plus,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCanvasInteractive } from './CanvasInteractiveContext'
import { TypewriterText } from './TypewriterText'
import { useCanvasGuidedMode } from '@/hooks/useCanvasGuidedMode'
import { useCreateAndLinkItem } from '@/hooks/useCanvasInstances'
import type { CanvasSection } from '@/components/canvas/canvasTemplates'
import { alpha } from '@/components/canvas/canvasTheme'

export function GuidedModeView() {
  const {
    selectedTemplate,
    sectionStats,
    sectionItems,
    canvasContext,
    interactiveConfig,
    selectedInstanceUri,
    guidedTargetSection,
    clearGuidedTarget,
    accountId,
    projectId,
    apiKey,
  } = useCanvasInteractive()

  const questions = interactiveConfig.guidedQuestions || []

  const {
    currentStep,
    totalSteps,
    currentQuestion,
    resolvedQuestion,
    resolvedHint,
    suggestions,
    isLoadingSuggestions,
    goToStep,
    goNext,
    goPrev,
    isFirst,
    isLast,
    completedSections,
    fetchSuggestions,
  } = useCanvasGuidedMode({
    questions,
    sectionStats,
    sectionItems,
    canvasContext,
    accountId,
    projectId,
    canvasInstanceUri: selectedInstanceUri,
    apiKey,
    initialSectionId: guidedTargetSection,
  })

  const createAndLink = useCreateAndLinkItem()
  const [customInput, setCustomInput] = useState('')
  const [addedLabels, setAddedLabels] = useState<Set<string>>(new Set())
  const [pendingLabel, setPendingLabel] = useState<string | null>(null)

  // Clear guided target after initial navigation
  if (guidedTargetSection) clearGuidedTarget()

  // Find section definition for current question
  const currentSection = selectedTemplate.sections.find(
    (s) => s.id === currentQuestion?.sectionId,
  )

  // Current section items
  const currentSectionItemCount = currentQuestion
    ? sectionStats[currentQuestion.sectionId]?.count || 0
    : 0

  const handleAddEntity = useCallback(
    async (label: string, source: 'user' | 'ai' = 'user', confidence?: number) => {
      if (!selectedInstanceUri || !currentQuestion || !currentSection) return

      // classUris are full URIs like "http://...#CustomerSegment" — API expects short name
      const fullUri = currentSection.classUris[0] || ''
      const entityType = fullUri.includes('#') ? fullUri.split('#').pop()! : fullUri
      setPendingLabel(label)

      createAndLink.mutate(
        {
          canvasUri: selectedInstanceUri,
          sectionId: currentQuestion.sectionId,
          label,
          entityType,
          source,
          confidence,
        },
        {
          onSuccess: () => {
            setAddedLabels((prev) => new Set([...prev, label]))
            setPendingLabel(null)
          },
          onError: () => {
            setPendingLabel(null)
          },
        },
      )
    },
    [selectedInstanceUri, currentQuestion, currentSection, createAndLink],
  )

  const handleCustomSubmit = useCallback(() => {
    const trimmed = customInput.trim()
    if (!trimmed) return
    handleAddEntity(trimmed)
    setCustomInput('')
  }, [customInput, handleAddEntity])

  if (!currentQuestion || !currentSection) return null

  const SectionIcon = currentSection.icon

  return (
    <div className="flex flex-col h-full">
      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5 py-3 border-b border-border-subtle bg-warm-50">
        {Array.from({ length: totalSteps }, (_, i) => {
          const q = questions.sort((a, b) => a.order - b.order)[i]
          const isFilled = q ? (sectionStats[q.sectionId]?.count || 0) > 0 : false
          const isCurrent = i === currentStep

          return (
            <button
              key={i}
              onClick={() => goToStep(i)}
              className="relative"
              title={q?.sectionId}
            >
              <div
                className={`
                  w-2.5 h-2.5 rounded-full transition-all duration-200
                  ${isCurrent ? 'ring-2 ring-offset-1' : ''}
                `}
                style={{
                  backgroundColor: isFilled
                    ? currentSection.color
                    : isCurrent
                      ? alpha(currentSection.color, 0.38)
                      : 'var(--color-warm-200)',
                  ringColor: isCurrent ? currentSection.color : undefined,
                }}
              />
            </button>
          )
        })}
        <span className="ml-3 text-xs text-text-muted">
          {completedSections}/{totalSteps} sections filled
        </span>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-6 py-8">
          {/* Section badge */}
          <div className="flex items-center gap-2 mb-6">
            <div
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: alpha(currentSection.color, 0.12), color: currentSection.color }}
            >
              <SectionIcon className="w-3.5 h-3.5" />
              {currentSection.title}
              {currentSectionItemCount > 0 && (
                <span className="ml-1 opacity-60">({currentSectionItemCount} items)</span>
              )}
            </div>
            <span className="text-xs text-text-muted">
              Step {currentStep + 1} of {totalSteps}
            </span>
          </div>

          {/* Question */}
          <h2 className="text-xl font-display font-semibold text-text mb-3 leading-relaxed">
            <TypewriterText text={resolvedQuestion} speed={15} key={currentStep} />
          </h2>

          {/* Hint */}
          <p className="text-sm text-text-secondary mb-8 leading-relaxed">{resolvedHint}</p>

          {/* AI Suggestions */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-business" />
              <span className="text-xs font-medium text-business">AI Suggestions</span>
              {!isLoadingSuggestions && (
                <button
                  onClick={fetchSuggestions}
                  className="ml-auto text-text-muted hover:text-text-secondary transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
              )}
            </div>

            {isLoadingSuggestions ? (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="w-4 h-4 text-business animate-spin" />
                <span className="text-xs text-text-muted">Generating suggestions...</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion) => {
                  const isAdded = addedLabels.has(suggestion.label)
                  const isPending = pendingLabel === suggestion.label
                  return (
                    <button
                      key={suggestion.id}
                      disabled={isAdded || isPending || createAndLink.isPending}
                      onClick={() => handleAddEntity(
                        suggestion.label,
                        'ai',
                        suggestion.confidence === 'high' ? 85 : suggestion.confidence === 'medium' ? 65 : 40,
                      )}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                        border transition-all duration-200
                        ${
                          isAdded
                            ? 'bg-market/10 border-market/30 text-market cursor-default'
                            : isPending
                              ? 'bg-business/5 border-business/30 text-business cursor-wait'
                              : 'bg-white border-border-subtle hover:border-business hover:shadow-sm cursor-pointer'
                        }
                      `}
                    >
                      {isAdded ? (
                        <Check className="w-3 h-3 text-market" />
                      ) : isPending ? (
                        <Loader2 className="w-3 h-3 text-business animate-spin" />
                      ) : (
                        <Plus className="w-3 h-3 text-business" />
                      )}
                      {suggestion.label}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Free-text input */}
          <div className="flex gap-2 mb-8">
            <input
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
              placeholder={`Add your own ${currentSection.title.toLowerCase()} item...`}
              className="flex-1 px-3 py-2 text-sm rounded-lg border border-border-subtle bg-white
                focus:outline-none focus:border-business focus:ring-1 focus:ring-business/20
                placeholder:text-text-muted"
            />
            <Button
              size="sm"
              onClick={handleCustomSubmit}
              disabled={!customInput.trim() || createAndLink.isPending}
              className="bg-business text-white hover:bg-business/90"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add
            </Button>
          </div>

          {/* Current section items preview */}
          {currentSectionItemCount > 0 && (
            <div className="border border-border-subtle rounded-lg p-4 bg-warm-50">
              <h4 className="text-xs font-medium text-text-secondary mb-2">
                Current items in {currentSection.title}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {(sectionItems[currentQuestion.sectionId] || []).map((item, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 text-xs rounded-md bg-white border"
                    style={{
                      borderColor: alpha(currentSection.color, 0.25),
                      color: 'var(--color-text)',
                    }}
                  >
                    {item.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation footer */}
      <div className="flex items-center justify-between px-6 py-3 border-t border-border-subtle bg-warm-50">
        <Button
          variant="ghost"
          size="sm"
          onClick={goPrev}
          disabled={isFirst}
          className="text-text-secondary"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={goNext}
          disabled={isLast}
          className="text-text-secondary"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  )
}
